import React, { useCallback, useMemo } from 'react';
import { Text } from '@wearejh/rx-form';
import { useFormApi } from '@wearejh/rx-form/lib/hooks/useFormApi';

import { Button } from '../Button/Button';

import classes from './Forms.scss';

type Props = {
    id: string;
    field: string;
    initialValue?: number | string;
    minValue?: number | null;
    maxValue?: number | null;
};

export const QtySelect = React.memo(function QtySelect(props: Props) {
    const { field, id } = props;
    const MAX_VALUE = props.maxValue ?? Infinity;
    const MIN_VALUE = props.minValue ?? 0;
    const api = useFormApi();
    const inc = useCallback(() => {
        const curr = api.getValue(field);
        if (isPositiveInteger(curr)) {
            const parsed = parseInt(curr);
            if (parsed < MAX_VALUE) {
                api.setValue(field, String(parseInt(curr) + 1));
            }
        }
    }, [MAX_VALUE, api, field]);
    const dec = useCallback(() => {
        const curr = api.getValue(field);
        if (isPositiveInteger(curr)) {
            const parsed = parseInt(curr);
            if (parsed > MIN_VALUE) {
                api.setValue(field, String(parsed - 1));
            }
        }
    }, [MIN_VALUE, api, field]);
    const onBlur = useCallback(() => {
        const v = api.getValue(field);
        if (v === undefined) {
            api.setValue(field, '0');
            return;
        }
        if (isPositiveInteger(v)) {
            const parsed = parseInt(v);
            if (parsed > MAX_VALUE) {
                api.setValue(field, String(MAX_VALUE));
            }
        }
    }, [MAX_VALUE, api, field]);
    const onKeyUp = useCallback(
        (evt) => {
            switch (evt.code) {
                case 'ArrowDown':
                    return dec();
                case 'ArrowUp':
                    return inc();
            }
        },
        [dec, inc],
    );
    const mask = useMemo(() => intAndMax(props.maxValue ?? Infinity), [props.maxValue]);
    return (
        <div className={classes.qty}>
            <Button onClick={dec} variant="light" className={classes.qtyButton} data-test-id="product-quantity-minus">
                -
            </Button>
            <Text
                className={classes.qtyInput}
                id={id}
                field={field}
                type="number"
                pattern="\d*"
                mask={mask}
                initialValue={String(props.initialValue ?? 0)}
                autoComplete="off"
                onBlur={onBlur}
                onKeyUp={onKeyUp}
                data-test-id="product-quantity-input"
            />
            <Button onClick={inc} variant="light" className={classes.qtyButton} data-test-id="product-quantity-plus">
                +
            </Button>
        </div>
    );
});

function intAndMax(max = Infinity) {
    return function (value) {
        const next = onlyIntInput(value);
        const asInt = parseInt(next);
        if (asInt > max) return String(max);
        return next;
    };
}

export function onlyIntInput(value) {
    const nums = isPositiveInteger(value);
    if (!nums) {
        return value.replace(/[^0-9]/g, '');
    }
    return value;
}

export function isPositiveInteger(n): boolean {
    return /^\d+$/.test(String(n));
}
